import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock } from 'lucide-react';
import { Achievement, RARITY_CONFIG, CATEGORY_LABELS, RarityDjayWrapper } from '../progress/AchievementsSection';
import { getAchievementIllustration } from '../../mascot';
import { VariantUnlockInfo } from '../../mascot/useVariantUnlocks';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
// Canonical accessory-name map (single source of truth) — no longer a stale
// local copy. Importing this keeps new cosmetics (cap/sunglasses/goldChain)
// labelled correctly on the card backs.
import { ACCESSORY_NAMES } from '../../mascot/useDjayCustomization';
interface FlippableAchievementCardProps {
  achievement: Achievement & {
    isUnlocked: boolean;
    progress?: {
      current: number;
      max: number;
      label: string;
    };
  };
  variantInfo: VariantUnlockInfo | null;
}
function FlippableAchievementCard({
  achievement,
  variantInfo
}: FlippableAchievementCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const {
    shouldAnimate
  } = useMotionBudget();
  const Illustration = getAchievementIllustration(achievement.id);
  const styles = RARITY_CONFIG[achievement.rarity];
  const handleFlip = () => setIsFlipped(!isFlipped);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleFlip();
    }
  };
  const progressPct = achievement.progress && achievement.progress.max > 0 ? achievement.progress.current / achievement.progress.max * 100 : 0;
  // A card is a "reward-look" specimen when it grants a cosmetic (a colour
  // variant and/or an accessory). For those cards the Djay shown is the REWARD
  // look, not the learner's current look — so once unlocked we surface a subtle
  // badge that makes that deliberate difference legible (resolves the "alles
  // wordt rood behalve hier"-verwarring). See MASCOT_SSOT_ARCHITECTURE_AUDIT_2026 §5.3.
  const grantsCosmetic = !!achievement.djayVariantUnlocked || !!achievement.accessory && achievement.accessory !== 'none';
  const showRewardLookBadge = grantsCosmetic && achievement.isUnlocked;
  // If motion is disabled, we just crossfade the faces instead of 3D flipping
  const frontOpacity = !shouldAnimate && isFlipped ? 0 : 1;
  const backOpacity = !shouldAnimate && !isFlipped ? 0 : 1;
  const pointerEventsFront = isFlipped ? 'none' : 'auto';
  const pointerEventsBack = isFlipped ? 'auto' : 'none';
  return <div className={`relative w-full aspect-[3/4] cursor-pointer group ${shouldAnimate ? 'perspective-1000' : ''}`} onClick={handleFlip} onKeyDown={handleKeyDown} role="button" tabIndex={0} aria-label={`${achievement.name}. ${achievement.isUnlocked ? 'Ontgrendeld' : 'Vergrendeld'}. Klik om om te draaien.`} data-id="element-1029">
      <motion.div className={`w-full h-full relative ${shouldAnimate ? 'preserve-3d' : ''}`} animate={shouldAnimate ? {
      rotateY: isFlipped ? 180 : 0
    } : {}} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20
    }} data-id="element-1030">
        {/* FRONT FACE */}
        <div className={`absolute inset-0 rounded-2xl border-2 flex flex-col transition-opacity duration-300
            ${shouldAnimate ? 'backface-hidden' : ''} 
            ${achievement.isUnlocked ? `${styles.cardBg} ${styles.cardBorder} ${styles.glowColor}` : 'bg-slate-50 border-slate-200'}
          `} style={{
        opacity: frontOpacity,
        pointerEvents: pointerEventsFront as any
      }} data-id="element-1031">
          <div className="flex-1 relative flex items-center justify-center p-3 overflow-hidden rounded-t-2xl" data-id="element-1032">
            {showRewardLookBadge && <span className="absolute top-2 left-2 z-20 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-900/75 text-white backdrop-blur-sm" title="Djay draagt hier de beloning — niet je eigen gekozen look" data-id="element-1033">
                Beloning-look
              </span>}
            {/* The illustration fills the image area and renders its OWN
             locked silhouette + lock badge when locked — so we pass the
             real state props (previously omitted, which made every card
             fall back to a tiny greyed silhouette) and let it own the
             locked look. No separate card-level lock badge → no double
             lock. */}
            <div className="w-full h-full flex items-center justify-center transition-transform duration-300 group-hover:scale-[1.03]" data-id="element-1034">
              {Illustration ? <Illustration achievementId={achievement.id} variant={achievement.djayVariantUnlocked || 'default'} isUnlocked={achievement.isUnlocked} rarity={achievement.rarity === 'secret' ? 'legendary' : achievement.rarity} size="lg" animate={shouldAnimate && achievement.isUnlocked} className="!max-w-[150px] !max-h-[150px] w-full" data-id="element-1035" /> : (
            /* No dedicated illustration for this achievement — render the
            reward Djay (wearing its accessory/variant) as the card art.
            RarityDjayWrapper draws at a compact 'sm' size, so we scale
            the wrapper up to fill the same ~150px footprint the
            illustration-backed cards use. Without this, these cards read
            as tiny/empty next to their illustrated siblings. */
            <div className={`relative flex items-center justify-center w-full h-full ${!achievement.isUnlocked ? 'grayscale opacity-50' : ''}`} data-id="element-1036">
                  <div className="scale-[3.2] sm:scale-[3.6] origin-center" data-id="element-1037">
                    <RarityDjayWrapper rarity={achievement.rarity} emotion={achievement.isUnlocked ? achievement.djayEmotionUnlocked : achievement.djayEmotionLocked} variant={achievement.djayVariantUnlocked || 'default'} accessory={achievement.accessory} isUnlocked={achievement.isUnlocked} animate={false} data-id="element-1038" />
                  </div>
                  {!achievement.isUnlocked && <div className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white/85 backdrop-blur-sm shadow-sm flex items-center justify-center z-10" data-id="element-1039">
                      <Lock className="w-3.5 h-3.5 text-slate-400" data-id="element-1040" />
                    </div>}
                </div>)}
            </div>
          </div>

          <div className="p-3 bg-white/90 backdrop-blur-sm border-t border-slate-100 flex flex-col items-center text-center rounded-b-2xl z-10" data-id="element-1041">
            <span className="text-xl leading-none mb-1" data-id="element-1042">
              {achievement.emoji}
            </span>
            <h4 className={`text-sm sm:text-base font-black leading-tight line-clamp-2 ${achievement.isUnlocked ? styles.textColor : 'text-slate-500'}`} data-id="element-1043">
              {achievement.name}
            </h4>
            {achievement.isUnlocked ? <span className={`mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${styles.badgeBg} ${styles.badgeText}`} data-id="element-1044">
                {styles.label}
              </span> : <div className="mt-2 flex items-center gap-2 text-slate-400" data-id="element-1045">
                <div className="relative w-5 h-5 flex items-center justify-center" data-id="element-1046">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 24 24" data-id="element-1047">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" className="opacity-20" data-id="element-1048" />
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray={`${progressPct * 0.628} 62.8`} className="text-slate-500 transition-all duration-500" data-id="element-1049" />
                  </svg>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-1050">
                  {achievement.progress?.current || 0}/
                  {achievement.progress?.max || 1}
                </span>
              </div>}
          </div>
        </div>

        {/* BACK FACE */}
        <div className={`absolute inset-0 rounded-2xl border-2 flex flex-col p-3.5 bg-white overflow-hidden transition-opacity duration-300
            ${styles.cardBorder} 
            ${shouldAnimate ? 'backface-hidden rotate-y-180' : ''}
          `} style={{
        opacity: backOpacity,
        pointerEvents: pointerEventsBack as any
      }} data-id="element-1051">
          <div className="flex items-start justify-between gap-2 mb-2" data-id="element-1052">
            <span className="text-2xl leading-none" data-id="element-1053">{achievement.emoji}</span>
            <div className="flex flex-col items-end gap-1 min-w-0" data-id="element-1054">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider truncate max-w-full" data-id="element-1055">
                {CATEGORY_LABELS[achievement.category]}
              </span>
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${styles.badgeBg} ${styles.badgeText}`} data-id="element-1056">
                {styles.label}
              </span>
            </div>
          </div>

          <h4 className={`text-sm sm:text-base font-black leading-tight mb-1.5 line-clamp-2 ${styles.textColor}`} data-id="element-1057">
            {achievement.name}
          </h4>
          <p className="text-xs font-medium text-slate-600 mb-2 flex-1 min-h-0 line-clamp-3" data-id="element-1058">
            {achievement.description}
          </p>

          {achievement.progress && achievement.progress.max > 0 && <div className="mb-2 flex-shrink-0" data-id="element-1059">
              <div className="flex justify-between text-[10px] font-bold text-slate-400 mb-1" data-id="element-1060">
                <span data-id="element-1061">Voortgang</span>
                <span data-id="element-1062">
                  {achievement.progress.current} / {achievement.progress.max}
                </span>
              </div>
              <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-1063">
                <div className={`h-full rounded-full ${achievement.isUnlocked ? 'bg-emerald-400' : 'bg-amber-400'}`} style={{
              width: `${progressPct}%`
            }} data-id="element-1064" />
              </div>
            </div>}

          <div className="bg-slate-50 rounded-lg px-2.5 py-2 border border-slate-100 flex-shrink-0" data-id="element-1065">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5" data-id="element-1066">
              Levert op:
            </span>
            <div className="flex flex-col gap-0.5" data-id="element-1067">
              {achievement.djayVariantUnlocked && variantInfo && <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5 truncate" data-id="element-1068">
                  <span className="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0" data-id="element-1069" />
                  <span className="truncate" data-id="element-1070">{variantInfo.name}</span>
                </span>}
              {achievement.accessory && achievement.accessory !== 'none' && <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5 truncate" data-id="element-1071">
                  <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" data-id="element-1072" />
                  <span className="truncate" data-id="element-1073">
                    {ACCESSORY_NAMES[achievement.accessory] || achievement.accessory}
                  </span>
                </span>}
              {!achievement.djayVariantUnlocked && (!achievement.accessory || achievement.accessory === 'none') && <span className="text-xs font-bold text-slate-500" data-id="element-1074">
                    Eeuwige roem
                  </span>}
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
}
interface AchievementCategoryGroupProps {
  title: string;
  achievements: (Achievement & {
    isUnlocked: boolean;
    progress?: {
      current: number;
      max: number;
      label: string;
    };
  })[];
  mascotVariants: VariantUnlockInfo[];
}
export function AchievementCategoryGroup({
  title,
  achievements,
  mascotVariants
}: AchievementCategoryGroupProps) {
  // Sort: Unlocked first, then by progress percentage descending
  const sortedAchievements = useMemo(() => {
    return [...achievements].sort((a, b) => {
      if (a.isUnlocked && !b.isUnlocked) return -1;
      if (!a.isUnlocked && b.isUnlocked) return 1;
      const pctA = a.progress && a.progress.max > 0 ? a.progress.current / a.progress.max : 0;
      const pctB = b.progress && b.progress.max > 0 ? b.progress.current / b.progress.max : 0;
      return pctB - pctA;
    });
  }, [achievements]);
  const unlockedCount = achievements.filter(a => a.isUnlocked).length;
  const groupPct = achievements.length > 0 ? Math.round(unlockedCount / achievements.length * 100) : 0;
  const isComplete = unlockedCount === achievements.length;
  if (achievements.length === 0) return null;
  return <div className="space-y-4" data-id="element-1075">
      {/* Group header — title + a compact progress meter so each category's
              completeness is scannable at a glance, instead of a bare ratio pill
              floating on a thin rule. */}
      <div className="flex items-center justify-between gap-3 pb-2.5 border-b-2 border-slate-100" data-id="element-1076">
        <h3 className="text-lg font-black text-slate-800 truncate" data-id="element-1077">{title}</h3>
        <div className="flex items-center gap-2.5 flex-shrink-0" data-id="element-1078">
          <div className="hidden sm:block w-20 h-2 bg-slate-100 rounded-full overflow-hidden" aria-hidden="true" data-id="element-1079">
            <div className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-emerald-400' : 'bg-amber-400'}`} style={{
            width: `${groupPct}%`
          }} data-id="element-1080" />
          </div>
          <span className={`text-sm font-bold px-3 py-1 rounded-full tabular-nums ${isComplete ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-1081">
            {unlockedCount} / {achievements.length}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 overflow-visible" data-id="element-1082">
        <AnimatePresence mode="popLayout" data-id="element-1083">
          {sortedAchievements.map(achievement => {
          const variantInfo = achievement.djayVariantUnlocked ? mascotVariants.find(v => v.id === achievement.djayVariantUnlocked) || null : null;
          return <motion.div key={achievement.id} layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.2
          }} data-id="element-1084">
                <FlippableAchievementCard achievement={achievement} variantInfo={variantInfo} data-id="element-1085" />
              </motion.div>;
        })}
        </AnimatePresence>
      </div>
    </div>;
}